import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Quote, BookOpen } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { images } from "../data/images";
import Reveal from "../components/Reveal";

const Home = () => {
    const { t, lang } = useLanguage();

    return (
        <main data-testid="home-page" className="bg-white">
            {/* ============ HERO ============ */}
            <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
                <img
                    src={images.heroHome}
                    alt="SODECO humanitarian mission"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/30 to-black/60" />

                <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28 text-white">
                    <Reveal>
                        <span
                            className="text-[11px] tracking-[0.4em] uppercase mb-6 inline-flex items-center gap-3"
                            style={{ color: "var(--sodeco-yellow)" }}
                            data-testid="hero-eyebrow"
                        >
                            <span className="yellow-rule" /> {t.home.eyebrow}
                        </span>
                    </Reveal>
                    <Reveal delay={120}>
                        <h1
                            data-testid="hero-title"
                            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.05]"
                        >
                            {t.home.heroTitle}
                        </h1>
                    </Reveal>
                    <Reveal delay={240}>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-6 max-w-xl text-base md:text-lg text-white/85 font-light"
                        >
                            {t.home.heroSubtitle}
                        </p>
                    </Reveal>
                    <Reveal delay={360}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/missions" data-testid="hero-cta-missions" className="btn-pill">
                                {t.home.ctaPrimary} <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="hero-cta-contact"
                                className="text-sm uppercase tracking-[0.25em] text-white hover:text-[var(--sodeco-yellow)] transition-colors"
                            >
                                {t.home.ctaSecondary} →
                            </Link>
                        </div>
                    </Reveal>
                </div>

                <a
                    href="#about"
                    aria-label="scroll"
                    className="hidden md:flex absolute bottom-8 right-8 z-10 w-12 h-12 rounded-full border border-white/40 items-center justify-center text-white hover:bg-[var(--sodeco-yellow)] hover:text-black hover:border-[var(--sodeco-yellow)] transition-all"
                >
                    <ArrowDown size={16} />
                </a>
            </section>

            {/* ============ ENCYCLOPEDIA / ABOUT ============ */}
            <section
                id="about"
                data-testid="about-section"
                className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <Reveal>
                            <span className="text-[11px] tracking-[0.4em] uppercase text-[var(--sodeco-muted)]">
                                {t.home.aboutEyebrow}
                            </span>
                            <div className="mt-3"><span className="yellow-rule" /></div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2
                                data-testid="about-title"
                                className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-light"
                            >
                                {t.home.aboutTitle}
                            </h2>
                        </Reveal>
                        <Reveal delay={120}>
                            <p className="mt-8 text-lg leading-relaxed text-[var(--sodeco-ink)]">{t.home.aboutLead}</p>
                        </Reveal>
                        <div className="mt-6 grid md:grid-cols-2 gap-8 text-[var(--sodeco-muted)]">
                            <Reveal delay={200}><p className="leading-relaxed">{t.home.aboutP1}</p></Reveal>
                            <Reveal delay={280}><p className="leading-relaxed">{t.home.aboutP2}</p></Reveal>
                        </div>
                    </div>
                </div>

                <div className="mt-20 grid grid-cols-12 gap-4 md:gap-6">
                    {images.encyclopedia.map((img, idx) => (
                        <Reveal
                            key={idx}
                            delay={idx * 90}
                            className={
                                idx === 0
                                    ? "col-span-12 md:col-span-7 row-span-2"
                                    : idx === 1
                                    ? "col-span-6 md:col-span-5"
                                    : idx === 2
                                    ? "col-span-6 md:col-span-3"
                                    : "col-span-12 md:col-span-2"
                            }
                        >
                            <figure className="group relative overflow-hidden bg-[var(--sodeco-surface)] h-full">
                                <img
                                    src={img.src}
                                    alt={lang === "es" ? img.caption_es : img.caption_en}
                                    className={`w-full ${idx === 0 ? "h-[460px]" : "h-[220px]"} object-cover hover-grow`}
                                    loading="lazy"
                                />
                                <figcaption className="absolute left-4 bottom-4 right-4 text-xs uppercase tracking-[0.2em] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                    {lang === "es" ? img.caption_es : img.caption_en}
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ============ STATS ============ */}
            <section data-testid="stats-section" className="bg-[var(--sodeco-surface)] border-y border-[var(--sodeco-border)]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                    <Reveal>
                        <h3 className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)] mb-12">
                            {t.home.statsTitle}
                        </h3>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
                        {t.home.stats.map((s, i) => (
                            <Reveal key={i} delay={i * 120}>
                                <div data-testid={`stat-${i}`} className="border-l-2 pl-6" style={{ borderColor: "var(--sodeco-yellow)" }}>
                                    <div className="font-serif text-5xl md:text-6xl font-light">{s.value}</div>
                                    <div className="mt-2 text-sm text-[var(--sodeco-muted)] leading-relaxed">{s.label}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ PILLARS ============ */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <img src={images.encyclopediaWide} alt="" className="w-full h-[520px] object-cover" loading="lazy" />
                    </Reveal>
                </div>
                <div className="lg:col-span-7">
                    <Reveal>
                        <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)]">{t.home.pillarsTitle}</span>
                    </Reveal>
                    <Reveal delay={120}>
                        <h2 className="mt-4 font-serif text-3xl md:text-5xl font-light leading-[1.1]">
                            {t.home.aboutTitle.split(" ").slice(0, 3).join(" ")}…
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-8">
                        {t.home.pillars.map((p, i) => (
                            <Reveal key={p.title} delay={i * 110}>
                                <div data-testid={`pillar-${i}`} className="border-t border-[var(--sodeco-border)] pt-5">
                                    <div className="flex items-baseline justify-between mb-2">
                                        <h4 className="font-serif text-2xl">{p.title}</h4>
                                        <span className="font-mono text-xs text-[var(--sodeco-muted)]">0{i + 1}</span>
                                    </div>
                                    <p className="text-sm leading-relaxed text-[var(--sodeco-muted)]">{p.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ QUOTE ============ */}
            <section className="bg-white py-24 md:py-32 border-t border-[var(--sodeco-border)]">
                <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <blockquote data-testid="quote" className="font-serif text-3xl md:text-5xl font-light leading-[1.15]">
                            {t.home.quote}
                        </blockquote>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="mt-8 text-xs uppercase tracking-[0.3em] text-[var(--sodeco-muted)]">{t.home.quoteAuthor}</div>
                    </Reveal>
                </div>
            </section>

            {/* ============ TESTIMONIOS ============ */}
            <section
                data-testid="testimonials-section"
                className="bg-[var(--sodeco-surface)] border-t border-[var(--sodeco-border)] py-24 md:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                        <div className="md:col-span-4">
                            <Reveal>
                                <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)]">
                                    {t.home.testimonialsEyebrow}
                                </span>
                                <div className="mt-3"><span className="yellow-rule" /></div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8">
                            <Reveal>
                                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-light">
                                    {t.home.testimonialsTitle}
                                </h2>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
                        {t.home.testimonials.map((tm, i) => (
                            <Reveal key={i} delay={i * 130}>
                                <article
                                    data-testid={`testimonial-${i}`}
                                    className="relative bg-white p-8 md:p-10 border border-[var(--sodeco-border)] h-full flex flex-col"
                                >
                                    <Quote
                                        size={28}
                                        className="absolute -top-3 left-8 bg-[var(--sodeco-yellow)] text-[var(--sodeco-ink)] rounded-full p-1"
                                    />
                                    <p className="font-serif text-xl leading-[1.4] text-[var(--sodeco-ink)] flex-1">
                                        {tm.quote}
                                    </p>
                                    <div className="mt-8 flex items-center gap-4 pt-6 border-t border-[var(--sodeco-border)]">
                                        <span className="relative inline-flex w-12 h-12 rounded-full overflow-hidden ring-2 ring-[var(--sodeco-yellow)] ring-offset-2 ring-offset-white">
                                            <img
                                                src={images.testimonials[i % images.testimonials.length]}
                                                alt={tm.name}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </span>
                                        <div>
                                            <div className="font-serif text-lg leading-tight">{tm.name}</div>
                                            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--sodeco-muted)] mt-1">
                                                {tm.role}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FRASES BÍBLICAS ============ */}
            <section
                data-testid="bible-section"
                className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                    <div className="md:col-span-4">
                        <Reveal>
                            <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)] flex items-center gap-2">
                                <BookOpen size={14} /> {t.home.bibleEyebrow}
                            </span>
                            <div className="mt-3"><span className="yellow-rule" /></div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-light">
                                {t.home.bibleTitle}
                            </h2>
                        </Reveal>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    {t.home.bibleVerses.map((v, i) => (
                        <Reveal key={i} delay={i * 110}>
                            <article
                                data-testid={`bible-verse-${i}`}
                                className="relative pl-6 md:pl-8 border-l-2 hover:bg-[var(--sodeco-yellow-soft)]/40 transition-colors py-3"
                                style={{ borderColor: "var(--sodeco-yellow)" }}
                            >
                                <p className="font-serif text-xl md:text-2xl leading-[1.4] italic text-[var(--sodeco-ink)]">
                                    {v.text}
                                </p>
                                <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[var(--sodeco-yellow-deep)] font-medium">
                                    {v.ref}
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ============ IMPACTO REPÚBLICA DOMINICANA ============ */}
            <section
                data-testid="dr-section"
                className="bg-[var(--sodeco-surface)] border-y border-[var(--sodeco-border)] py-24 md:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                        <div className="md:col-span-4">
                            <Reveal>
                                <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)]">
                                    {t.home.drEyebrow}
                                </span>
                                <div className="mt-3"><span className="yellow-rule" /></div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8">
                            <Reveal>
                                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-light">
                                    {t.home.drTitle}
                                </h2>
                            </Reveal>
                            <Reveal delay={120}>
                                <p className="mt-8 text-lg leading-relaxed text-[var(--sodeco-ink)]">{t.home.drLead}</p>
                            </Reveal>
                            <div className="mt-6 grid md:grid-cols-2 gap-8 text-[var(--sodeco-muted)]">
                                <Reveal delay={200}><p className="leading-relaxed">{t.home.drP1}</p></Reveal>
                                <Reveal delay={280}><p className="leading-relaxed">{t.home.drP2}</p></Reveal>
                            </div>
                        </div>
                    </div>

                    {/* Small images grid – mismo margen que la sección de enciclopedia */}
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {images.dominicanRepublic.map((img, idx) => (
                            <Reveal key={idx} delay={idx * 90}>
                                <figure className="group relative overflow-hidden border border-[var(--sodeco-border)] bg-white">
                                    <img
                                        src={img.src}
                                        alt={lang === "es" ? img.caption_es : img.caption_en}
                                        className="w-full h-[200px] md:h-[220px] object-cover hover-grow"
                                        loading="lazy"
                                    />
                                    <figcaption className="absolute inset-x-0 bottom-0 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-white bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                        {lang === "es" ? img.caption_es : img.caption_en}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>

                    {/* Stats DR */}
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-10 pt-12 border-t border-[var(--sodeco-border)]">
                        {t.home.drStats.map((s, i) => (
                            <Reveal key={i} delay={i * 110}>
                                <div data-testid={`dr-stat-${i}`} className="border-l-2 pl-5" style={{ borderColor: "var(--sodeco-yellow)" }}>
                                    <div className="font-serif text-4xl md:text-5xl font-light">{s.value}</div>
                                    <div className="mt-2 text-sm text-[var(--sodeco-muted)]">{s.label}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ STAFF / EQUIPO JERÁRQUICO ============ */}
            <section
                data-testid="staff-section"
                className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
                    <div className="md:col-span-4">
                        <Reveal>
                            <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)]">
                                {t.home.staffEyebrow}
                            </span>
                            <div className="mt-3"><span className="yellow-rule" /></div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-8">
                        <Reveal>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] font-light">
                                {t.home.staffTitle}
                            </h2>
                        </Reveal>
                        <Reveal delay={120}>
                            <p className="mt-6 max-w-xl text-[var(--sodeco-muted)] leading-relaxed">
                                {t.home.staffSubtitle}
                            </p>
                        </Reveal>
                    </div>
                </div>

                {/* Tier 1 - CEO */}
                <Reveal>
                    <div data-testid="staff-tier-ceo" className="flex flex-col items-center text-center">
                        <span className="relative inline-flex w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden ring-4 ring-[var(--sodeco-yellow)] ring-offset-4 ring-offset-white">
                            <img src={images.staff.ceo} alt={t.home.staff.ceo.name} className="w-full h-full object-cover" loading="lazy" />
                        </span>
                        <div className="mt-5 font-serif text-2xl">{t.home.staff.ceo.name}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-[var(--sodeco-yellow-deep)] font-medium">
                            {t.home.staff.ceo.role}
                        </div>
                    </div>
                </Reveal>

                {/* Vertical connector */}
                <div className="mx-auto my-10 w-px h-12" style={{ background: "var(--sodeco-yellow)" }} />

                {/* Tier 2 - Executive Director */}
                <Reveal delay={120}>
                    <div data-testid="staff-tier-exec" className="flex flex-col items-center text-center">
                        <span className="relative inline-flex w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden ring-2 ring-[var(--sodeco-yellow)] ring-offset-2 ring-offset-white">
                            <img
                                src={images.staff.executiveDirector}
                                alt={t.home.staff.executiveDirector.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </span>
                        <div className="mt-4 font-serif text-xl">{t.home.staff.executiveDirector.name}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[var(--sodeco-muted)]">
                            {t.home.staff.executiveDirector.role}
                        </div>
                    </div>
                </Reveal>

                <div className="mx-auto my-10 w-px h-12" style={{ background: "var(--sodeco-yellow)" }} />

                {/* Tier 3 - Managers */}
                <div data-testid="staff-tier-managers">
                    <Reveal>
                        <div className="text-center mb-8 text-[11px] uppercase tracking-[0.35em] text-[var(--sodeco-muted)]">
                            {t.home.staff.managersLabel}
                        </div>
                    </Reveal>
                    <div className="flex flex-wrap justify-center gap-10 md:gap-16">
                        {t.home.staff.managers.map((p, i) => (
                            <Reveal key={p.name} delay={i * 100}>
                                <div className="flex flex-col items-center text-center max-w-[140px]">
                                    <span className="relative inline-flex w-20 h-20 rounded-full overflow-hidden ring-2 ring-[var(--sodeco-yellow)]/70 ring-offset-2 ring-offset-white">
                                        <img src={images.staff.managers[i]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                    </span>
                                    <div className="mt-3 font-serif text-base leading-tight">{p.name}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)]">
                                        {p.role}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <div className="mx-auto my-10 w-px h-10" style={{ background: "var(--sodeco-yellow)" }} />

                {/* Tier 4 - Supervisors */}
                <div data-testid="staff-tier-supervisors">
                    <Reveal>
                        <div className="text-center mb-8 text-[11px] uppercase tracking-[0.35em] text-[var(--sodeco-muted)]">
                            {t.home.staff.supervisorsLabel}
                        </div>
                    </Reveal>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-12">
                        {t.home.staff.supervisors.map((p, i) => (
                            <Reveal key={p.name} delay={i * 90}>
                                <div className="flex flex-col items-center text-center max-w-[120px]">
                                    <span className="relative inline-flex w-16 h-16 rounded-full overflow-hidden ring-2 ring-[var(--sodeco-yellow)]/50 ring-offset-2 ring-offset-white">
                                        <img src={images.staff.supervisors[i]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                    </span>
                                    <div className="mt-3 font-serif text-sm leading-tight">{p.name}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--sodeco-muted)]">
                                        {p.role}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <div className="mx-auto my-10 w-px h-10" style={{ background: "var(--sodeco-yellow)" }} />

                {/* Tier 5 - Specialists */}
                <div data-testid="staff-tier-specialists">
                    <Reveal>
                        <div className="text-center mb-8 text-[11px] uppercase tracking-[0.35em] text-[var(--sodeco-muted)]">
                            {t.home.staff.specialistsLabel}
                        </div>
                    </Reveal>
                    <div className="flex flex-wrap justify-center gap-6 md:gap-10">
                        {t.home.staff.specialists.map((p, i) => (
                            <Reveal key={p.name} delay={i * 70}>
                                <div className="flex flex-col items-center text-center max-w-[110px]">
                                    <span className="relative inline-flex w-14 h-14 rounded-full overflow-hidden ring-1 ring-[var(--sodeco-yellow)]/60 ring-offset-1 ring-offset-white">
                                        <img src={images.staff.specialists[i]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                                    </span>
                                    <div className="mt-2.5 font-serif text-[13px] leading-tight">{p.name}</div>
                                    <div className="mt-0.5 text-[9px] uppercase tracking-[0.2em] text-[var(--sodeco-muted)]">
                                        {p.role}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
